import React, { useEffect, useRef, useState } from "react";
import { collection, getDocs, query, where, doc } from "firebase/firestore";
import { db } from "../Firebase";
import ReactDOM from "react-dom/client";
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText, Switch, Stack, Typography, TextField } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import mapboxgl from "mapbox-gl";
import SiteWorkersSidebar from "../components/SiteWorkersSidebar";
import WorkerClockingHistoryModal from "../components/WorkerClockingHistoryModal";
import workerProfile from "../Img/workerProfile.png";
import emptyImage from "../Img/empty.png";
import { useTranslation } from "react-i18next";
import dangerIcon from "../Img/danger.png";
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';

export default function AdminSitesMap() {
  const { t, i18n } = useTranslation();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [sites, setSites] = useState([]);
  const markersRef = useRef([]);
  const sitesRef = useRef([]);
  const violationsRef = useRef({});
  const hoverPopupRef = useRef(null);
  const hoveredSiteIdRef = useRef(null);
  const [workersCountBySite, setWorkersCountBySite] = useState({});
  const [selectedSite, setSelectedSite] = useState(null);
  const [hiddenSiteIds, setHiddenSiteIds] = useState([]);
  const [siteWorkers, setSiteWorkers] = useState([]);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedHistoryUserId, setSelectedHistoryUserId] = useState(null);
  const [violationsBySite, setViolationsBySite] = useState({}); 
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [siteFilterQuery, setSiteFilterQuery] = useState("");

  // Hide sites if wanted
  const hiddenSiteIdsRef = useRef([]);
  useEffect(() => {
    hiddenSiteIdsRef.current = hiddenSiteIds;
  }, [hiddenSiteIds]);

  // Reset search bar when closing the modal
  useEffect(() => {
    if (!openFilterModal) setSiteFilterQuery("");
  }, [openFilterModal]);



  // Helpers: 

  const isSiteHidden = (siteId) => hiddenSiteIds.includes(siteId);
  const toggleSiteHidden = (siteId) => {
    setHiddenSiteIds((prev) =>
      prev.includes(siteId) ? prev.filter((id) => id !== siteId) : [...prev, siteId]
    );
  };

  // --- Site end-date helpers ---
  const parseSiteEndDate = (raw) => {
    if (!raw) return null;

    // Firestore Timestamp (common)
    if (typeof raw?.toDate === "function") return raw.toDate();
    if (typeof raw?.seconds === "number") return new Date(raw.seconds * 1000);

    // Firestore exports sometimes use _seconds
    if (typeof raw?._seconds === "number") return new Date(raw._seconds * 1000);

    // Already a Date
    if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;

    // Number (e.g. 2030)
    if (typeof raw === "number" && Number.isFinite(raw)) {
      // treat as year if it's clearly a year
      if (raw >= 1900 && raw <= 3000) return new Date(raw, 0, 1);
      return null;
    }

    const s = String(raw).trim();

    // Accept "2030" (year only)
    if (/^\d{4}$/.test(s)) {
      const y = Number(s);
      return new Date(y, 0, 1);
    }

    // Accept "YYYY-MM-DD"
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, d] = s.split("-").map(Number);
      return new Date(y, m - 1, d);
    }

    // Accept "DD/MM/YYYY"
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
      const [d, m, y] = s.split("/").map(Number);
      // NOTE: we choose DD/MM/YYYY deterministically
      return new Date(y, m - 1, d);
    }

    // IMPORTANT: no Date(s) fallback (it causes mis-parsing -> yellow bug)
    return null;
  };

  const monthsBetweenTodayAnd = (futureDate) => {
    if (!futureDate) return null;
    const now = new Date();

    // If already expired (past), treat as 0 months remaining
    const f = futureDate;
    if (f.getTime() <= now.getTime()) return 0;

    let months =
      (f.getFullYear() - now.getFullYear()) * 12 +
      (f.getMonth() - now.getMonth());

    // If future day-of-month is greater than today day-of-month, count as extra partial month
    if (f.getDate() > now.getDate()) months += 0;
    // If it's less, then we haven't completed that month -> reduce by 1
    else if (f.getDate() < now.getDate()) months -= 1;

    return Math.max(0, months);
  };

  // returns one of: "safe" | "warning" | "danger"
  const getEndStatus = (siteEndDate) => {
    const d = parseSiteEndDate(siteEndDate);
    if (!d) return "safe";

    const now = new Date();

    // Hard guard: anything >= 2 years away is always safe (blue)
    if (d.getFullYear() >= now.getFullYear() + 2) return "safe";

    const m = monthsBetweenTodayAnd(d);
    if (m == null) return "safe";

    if (m >= 6) return "safe";
    if (m >= 3) return "warning";
    return "danger";
  };

  const getColorFromEndStatus = (endStatus) => {
    if (endStatus === "warning") return "#f59e0b";
    if (endStatus === "danger") return "#c93d3d";
    return "#124fa4"; // safe
  };



  const getTodayISO = () => {
    const now = new Date();
    const tzOffsetMs = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - tzOffsetMs).toISOString().slice(0, 10); // "YYYY-MM-DD"
  };

  const getTodayDMY = () => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = String(now.getFullYear());
    return `${dd}/${mm}/${yyyy}`; // "DD/MM/YYYY"
  };

  const buildTodayDateConstraints = () => {
    const iso = getTodayISO();  // "YYYY-MM-DD"
    const dmy = getTodayDMY();  // "DD/MM/YYYY"
    // If you ever end up having both formats equal (unlikely), avoid duplicate OR clauses.
    return iso === dmy ? [iso] : [iso, dmy];
  };


  const toTimeHHMMSS = (timeStr) => {
  if (!timeStr) return null;

  const s = String(timeStr).trim();

  // Format: "06 h 45 min 56 s"  (seconds optional)
  const hmsMatch = s.match(/(\d{1,2})\s*h\s*(\d{1,2})\s*min(?:\s*(\d{1,2})\s*s)?/i);
    if (hmsMatch) {
      const hh = String(hmsMatch[1]).padStart(2, "0");
      const mm = String(hmsMatch[2]).padStart(2, "0");
      const ss = String(hmsMatch[3] ?? "0").padStart(2, "0");
      return `${hh}:${mm}:${ss}`;
    }

    // Format: "06:45" or "06:45:56"
    if (!s.includes("AM") && !s.includes("PM") && s.includes(":")) {
      const parts = s.split(":").map((p) => p.trim());
      if (parts.length === 2) {
        const [hh, mm] = parts;
        return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;
      }
      if (parts.length === 3) {
        const [hh, mm, ss] = parts;
        return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
      }
    }

    // Format: "6:45 AM" / "06:45 PM"
    if (s.includes("AM") || s.includes("PM")) {
      const [time, mod] = s.split(" ");
      let [h, m] = time.split(":");
      if (h === "12") h = "00";
      if (mod === "PM") h = String(parseInt(h, 10) + 12);
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
    }

    return null;
  };

  const hhmmssToDate = (hhmmss) => new Date(`2000-01-01T${hhmmss}`);
    const toDisplayHHMM = (timeStr) => {
      const hhmmss = toTimeHHMMSS(timeStr);
      return hhmmss ? hhmmss.slice(0, 5) : "";
    };

  const computeHoursWorked = (clockInTime, clockOutTime) => {
    const inHHMMSS = toTimeHHMMSS(clockInTime);
    const outHHMMSS = toTimeHHMMSS(clockOutTime);
    if (!inHHMMSS || !outHHMMSS) return "";

    const inD = hhmmssToDate(inHHMMSS);
    const outD = hhmmssToDate(outHHMMSS);

    const diffMin = (outD - inD) / 60000;
    if (!Number.isFinite(diffMin) || diffMin < 0) return "";

    const hrs = Math.floor(diffMin / 60);
    const mins = Math.floor(diffMin % 60);
    return `${hrs}h ${mins}m`;
  };

  const normalizeType = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/[\s-_]/g, "");

  const pickClockEntry = (entries, wanted) => {
    const w = normalizeType(wanted); // "clockin" / "clockout"
    return entries.find((e) => normalizeType(e.type) === w) || null;
  };


  const handleOpenHistory = (worker) => {
    setSelectedHistoryUserId(worker?.id);
    setOpenHistoryModal(true);
  };

  function calculateDistanceKm(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const toRad = (x) => (x * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  const getSiteLatLng = (site) => {
    const loc = site?.location;

    // Case A: your custom object {lat, lng}
    const latA = loc?.lat;
    const lngA = loc?.lng;

    // Case B: Firestore GeoPoint {latitude, longitude}
    const latB = loc?.latitude;
    const lngB = loc?.longitude;

    const lat = Number(latA ?? latB);
    const lng = Number(lngA ?? lngB);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  };

  // Fetch MapBox Map
  useEffect(() => {

    const token = process.env.REACT_APP_MAPBOX_TOKEN;


    if (!token) {
      console.error("Missing Mapbox token. Add it to your .env file.");
      return;
    }

    mapboxgl.accessToken = token;

    // Prevent double-init if React re-renders
    if (mapRef.current) return;

    // 2) Create map
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/streets-v12", // similar to your screenshot
      center: [-73.5674, 45.5019], // Montreal (example)
      zoom: 11,
      preserveDrawingBuffer: true,
    });

    mapRef.current.on("load", () => {
      const map = mapRef.current;

      // Source for sites
      if (!map.getSource("sofben-sites")) {
        map.addSource("sofben-sites", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
      }

      // Halo ring (outer)
      if (!map.getLayer("sofben-sites-halo")) {
        map.addLayer({
          id: "sofben-sites-halo",
          type: "circle",
          source: "sofben-sites",
          paint: {
            "circle-radius": 12,
            "circle-color": [
              "match",
              ["get", "endStatus"],
              "safe", "#124fa4",
              "warning", "#f59e0b",
              "danger", "#c93d3d",
              "#124fa4"
            ],
            "circle-opacity": 0.15,
          },
        });
      }

      // Dot
      if (!map.getLayer("sofben-sites-dot")) {
        map.addLayer({
          id: "sofben-sites-dot",
          type: "circle",
          source: "sofben-sites",
          paint: {
            "circle-radius": 9,
            "circle-color": [
              "match",
              ["get", "endStatus"],
              "safe", "#124fa4",
              "warning", "#f59e0b",
              "danger", "#c93d3d",
              "#124fa4"
            ],
          },
        });
      }

      // Load danger icon image into Mapbox once
      if (!map.hasImage("sofben-danger-icon")) {
        const img = new Image(28, 28);
        img.crossOrigin = "anonymous";
        img.src = dangerIcon; // imported PNG gives a URL string in CRA

        img.onload = () => {
          if (!map.hasImage("sofben-danger-icon")) {
            map.addImage("sofben-danger-icon", img);
          }
        };
      }

      // Violation symbol (⚠️) shown next to dot
      if (!map.getLayer("sofben-sites-violation-icon")) {
        map.addLayer({
          id: "sofben-sites-violation-icon",
          type: "symbol",
          source: "sofben-sites",
          filter: ["==", ["get", "hasViolation"], true],
          layout: {
            "icon-image": "sofben-danger-icon",
            "icon-size": 0.6,
            "icon-offset": [18, -6],  // right of dot, slightly up
            "icon-anchor": "left",
            "icon-allow-overlap": true,
            "icon-ignore-placement": true,
          },
        });
      }

      // White border
      if (!map.getLayer("sofben-sites-stroke")) {
        map.addLayer({
          id: "sofben-sites-stroke",
          type: "circle",
          source: "sofben-sites",
          paint: {
            "circle-radius": 9,
            "circle-color": "rgba(0,0,0,0)",
            "circle-stroke-color": "#1da035ff",
            "circle-stroke-width": 3,
          },
        });
      }
    });

    // Optional: add zoom controls
    mapRef.current.addControl(new mapboxgl.NavigationControl(), "top-right");
    mapRef.current.addControl(new mapboxgl.FullscreenControl(), "top-right");

    class PrintControl {
      onAdd(map) {
        this._map = map;
        this._container = document.createElement("div");
        this._container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";

        const button = document.createElement("button");
        button.type = "button";
        button.title = t("Print map");

        // Render MUI icon inside Mapbox control
        const iconRoot = document.createElement("div");
        ReactDOM.createRoot(iconRoot).render(
          <LocalPrintshopIcon sx={{ fontSize: 20 }} />
        );

        button.appendChild(iconRoot);
        button.onclick = async () => {
          const map = this._map;
          const glCanvas = map?.getCanvas();
          if (!map || !glCanvas) return;

          // Small in-tab overlay instead of a separate window while preparing
          const overlay = document.createElement("div");
          overlay.style.cssText =
            "position:fixed;inset:0;background:rgba(0,0,0,.35);z-index:999999;display:flex;align-items:center;justify-content:center;";
          overlay.innerHTML = `
            <div style="background:#fff;padding:14px 16px;border-radius:12px;font-family:Arial;box-shadow:0 12px 30px rgba(0,0,0,.2)">
              ${t("Preparing map for print…")}
            </div>
          `;
          document.body.appendChild(overlay);

          try {
            // Ensure the map finished rendering
            const waitForIdle = () =>
              new Promise((resolve) => {
                let finished = false;

                const done = () => {
                  if (finished) return;
                  finished = true;
                  // double rAF = ensures the canvas has the latest frame (markers/layers included)
                  requestAnimationFrame(() => requestAnimationFrame(resolve));
                };

                const armIdle = () => {
                  // If Mapbox style is loaded, wait for the next fully-idle frame
                  map.once("idle", done);
                  // Force a render tick (helps after setData / style changes)
                  map.triggerRepaint();
                  // Safety fallback in case "idle" never fires (tiles still streaming, etc.)
                  setTimeout(done, 1500);
                };

                if (map.loaded()) {
                  armIdle();
                } else {
                  map.once("load", armIdle);
                  // Extra fallback if "load" is delayed for any reason
                  setTimeout(armIdle, 2000);
                }
              });

            await waitForIdle();

            // ---- Build image in memory (no new window yet) ----
            const out = document.createElement("canvas");
            out.width = glCanvas.width;
            out.height = glCanvas.height;

            const ctx = out.getContext("2d");
            if (!ctx) throw new Error("2D context not available");

            // Base map
            ctx.drawImage(glCanvas, 0, 0);

            // ratio CSS->device pixels
            const ratio = glCanvas.width / glCanvas.clientWidth;

            // Draw markers by projection
            const hiddenIds = hiddenSiteIdsRef.current || [];
            const sitesToDraw = (sitesRef.current || []).filter((s) => !hiddenIds.includes(s.id));
            const vMap = violationsRef.current || {};

            sitesToDraw.forEach((site) => {
              const coords = getSiteLatLng(site);
              if (!coords) return;

              const { lat, lng } = coords;
              const p = map.project([lng, lat]);

              const x = p.x * ratio;
              const y = p.y * ratio;

              const siteViolationCount = vMap?.[site.id]?.count ?? 0;
              const hasAlert = siteViolationCount > 0;

              // color MUST depend on end date (not violations)
              const endStatus = getEndStatus(site?.siteEndDate);
              const baseColor = getColorFromEndStatus(endStatus);

              // ring
              ctx.beginPath();
              ctx.arc(x, y, 12 * ratio, 0, Math.PI * 2);
              ctx.fillStyle = baseColor + "26"; // ~15% opacity (hex alpha)
              ctx.fill();

              // dot
              ctx.beginPath();
              ctx.arc(x, y, 9 * ratio, 0, Math.PI * 2);
              ctx.fillStyle = baseColor;
              ctx.fill();

              // white border
              ctx.lineWidth = 3 * ratio;
              ctx.strokeStyle = "#ffffff";
              ctx.stroke();

              // ⚠️ next to dot if violation exists (DO NOT change dot color)
              if (hasAlert) {
                ctx.font = `${16 * ratio}px Arial`;
                ctx.textBaseline = "middle";
                ctx.fillStyle = "#111827";
                ctx.fillText("⚠️", x + 12 * ratio, y); // right side of dot
              }
              });

            // Export to blob
            const finalBlob = await new Promise((resolve, reject) => {
              const t = setTimeout(() => reject(new Error("Export timeout (toBlob)")), 4000);
              out.toBlob((blob) => {
                clearTimeout(t);
                if (!blob) return reject(new Error("Export failed: blob is null (tainted canvas)"));
                resolve(blob);
              }, "image/png");
            });

            const finalUrl = URL.createObjectURL(finalBlob);

            // ---- Now open print window ONLY when the image is ready ----
            const printWin = window.open("", "_blank");
            if (!printWin) throw new Error("Popup blocked: allow popups for this site");

            // Write once, immediately, and print
            printWin.document.open();
            printWin.document.write(`
              <!doctype html>
              <html>
                <head>
                  <meta charset="utf-8" />
                  <title>${t("Print Map")}</title>
                  <style>
                    /* Force landscape by default */
                    @page { size: landscape; margin: 0; }

                    /* Print-time hardening (Chrome/Edge-friendly) */
                    @media print {
                      @page { size: landscape; margin: 0; }
                      html, body { width: 100%; height: 100%; margin: 0; padding: 0; }
                    }

                    html, body {
                      margin: 0;
                      padding: 0;
                      width: 100%;
                      height: 100%;
                      background: #fff;
                    }

                    .page {
                      width: 100vw;
                      height: 100vh;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                    }

                    img {
                      width: 100%;
                      height: 100%;
                      object-fit: contain;
                    }
                  </style>
                </head>
                <body>
                  <div class="page">
                    <img id="m" src="${finalUrl}" alt="${t("Map print")}" />
                  </div>
                  <script>
                    const img = document.getElementById("m");
                    img.onload = () => {
                      window.focus();
                      window.print();
                      window.onafterprint = () => window.close();
                    };
                  </script>
                </body>
              </html>
            `);
            printWin.document.close();

            // cleanup URL after a bit (safe)
            setTimeout(() => {
              try { URL.revokeObjectURL(finalUrl); } catch(e) {}
            }, 10_000);
          } catch (err) {
            console.error("Map print failed:", err);
            alert(`${t("Print failed")}: ${err?.message || err}`);
          } finally {
            overlay.remove();
          }
        };


        this._container.appendChild(button);
        const filterBtn = document.createElement("button");
        filterBtn.type = "button";
        filterBtn.title = t("Filter sites");
        filterBtn.className = "sofben-filter-btn";

        // Render icon
        const filterIconRoot = document.createElement("div");
        ReactDOM.createRoot(filterIconRoot).render(
          <FilterAltIcon sx={{ fontSize: 20 }} />
        );
        filterBtn.appendChild(filterIconRoot);

        filterBtn.onclick = () => {
          setOpenFilterModal(true);
        };

        this._container.appendChild(filterBtn);
        return this._container;
      }

      onRemove() {
        this._container.parentNode.removeChild(this._container);
        this._map = undefined;
      }
    }
    mapRef.current.addControl(new PrintControl(), "top-right");

    // Optional: test marker (remove later)
    // new mapboxgl.Marker()
    //   .setLngLat([-73.5674, 45.5019])
    //   .addTo(mapRef.current);

    // 3) Cleanup
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [i18n.language]);


  useEffect(() => {
    const printBtn = document.querySelector(".sofben-print-btn");
    if (printBtn) printBtn.title = t("Print map");

    const filterBtn = document.querySelector(".sofben-filter-btn");
    if (filterBtn) filterBtn.title = t("Filter sites");
  }, [i18n.language, t]);


  // Fetch construction sites
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const snap = await getDocs(collection(db, "ConstructionSites"));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setSites(list);
      } catch (err) {
        console.error("Failed to fetch ConstructionSites:", err);
      }
    };

    fetchSites();
  }, []);

  const buildPopupHtml = (site, siteViolationCount, workersCount) => {
    return `
      <div style="font-family: Inter, Arial, sans-serif; min-width: 240px;">
        <div style="font-size: 14px; font-weight: 800; margin-bottom: 6px;">
          ${site?.name ?? t("Construction Site")}
        </div>

        ${
          siteViolationCount > 0
            ? `<div style="display:inline-flex; align-items:center; gap:8px; padding:6px 10px; border-radius:999px; background:#fff7ed; color:#9a3412; font-size:12px; font-weight:800; margin-bottom:8px;">
                ⚠️ ${siteViolationCount} ${t("violations today")}
              </div>`
            : `<div style="display:inline-flex; align-items:center; gap:8px; padding:6px 10px; border-radius:999px; background:#ecfdf5; color:#065f46; font-size:12px; font-weight:800; margin-bottom:8px;">
                ✅ ${t("No violations today")}
              </div>`
        }

        <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px;">
          ${site?.location?.address ?? t("No address provided")}
        </div>

        <div style="display:flex; gap:8px; margin-top: 6px; align-items:center; flex-wrap:wrap;">
          <div style="padding:6px 10px; border-radius:999px; background:#f3f4f6; color:#111827; font-size:12px; display:flex; align-items:center; gap:6px;">
            <span style="font-size:14px; line-height:1;">👷</span>
            <b>${workersCount}</b>
            <span>${t("worker(s)")}</span>
          </div>

          <div style="padding:6px 10px; border-radius:999px; background:#f0faf6; color:#006d2c; font-size:12px;">
            <b>${t("Start")}:</b> ${site?.startTime ?? "--:--"}
          </div>

          <div style="padding:6px 10px; border-radius:999px; background:#fef2f2; font-size:12px; color:#991b1b;">
            <b>${t("End")}:</b> ${site?.endTime ?? "--:--"}
          </div>
        </div>

        <div style="margin-top:10px; font-size:12px; color:#111827;">
          <b>${t("Click marker")}</b> ${t("to view workers")}
        </div>
      </div>
    `;
  };


  // Display the constrcution site markers on the Map
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!map.getSource("sofben-sites")) return; 
    if (!sites || sites.length === 0) return;

    const features = sites
      .filter((site) => !isSiteHidden(site.id)) 
      .map((site) => {
        const coords = getSiteLatLng(site);
        if (!coords) return null;

        const siteViolationCount = violationsBySite?.[site.id]?.count ?? 0;
        const hasViolation = siteViolationCount > 0;

        // IMPORTANT: assumes your site document has siteEndDate field
        const endStatus = getEndStatus(site?.siteEndDate);

        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [coords.lng, coords.lat],
          },
          properties: {
            siteId: site.id,
            name: site?.name ?? "",
            address: site?.location?.address ?? "",
            violationCount: siteViolationCount,
            hasViolation,
            endStatus, 
            workersCount: workersCountBySite?.[site.id] ?? 0,
          },
        };
      })
      .filter(Boolean);

      const geojson = { type: "FeatureCollection", features };

      map.getSource("sofben-sites").setData(geojson);
  }, [sites, hiddenSiteIds, violationsBySite, workersCountBySite, i18n.language]);


useEffect(() => {
  const map = mapRef.current;
  if (!map) return;

  const clickableLayers = [
    "sofben-sites-halo",
    "sofben-sites-dot",
    "sofben-sites-stroke",
    "sofben-sites-violation-icon",
  ];

  const safeOff = (layerId, eventName, handler) => {
    try {
      map.off(eventName, layerId, handler);
    } catch (e) {
      // ignore (map style already destroyed or layer missing)
    }
  };

  const safeOn = (layerId, eventName, handler) => {
    try {
      map.on(eventName, layerId, handler);
      return true;
    } catch (e) {
      return false;
    }
  };

  const getSiteFromFeature = (feature) => {
    const siteId = feature?.properties?.siteId;
    if (!siteId) return null;
    return (sitesRef.current || []).find((s) => s.id === siteId) || null;
  };

  const onMouseEnter = () => {
    try {
      map.getCanvas().style.cursor = "pointer";
    } catch (e) {}
  };

  const onMouseLeave = () => {
    try {
      map.getCanvas().style.cursor = "";
    } catch (e) {}

    hoveredSiteIdRef.current = null;

    if (hoverPopupRef.current) {
      hoverPopupRef.current.remove();
      hoverPopupRef.current = null;
    }
  };

  const onMouseMove = (e) => {
    const f = e?.features?.[0];
    if (!f) return;

    const siteId = f.properties?.siteId;
    const site = getSiteFromFeature(f);
    if (!siteId || !site) return;

    const siteViolationCount = f.properties?.violationCount ?? 0;
    const workersCount = f.properties?.workersCount ?? 0;

    // If new site, rebuild popup; otherwise just move it
    if (hoveredSiteIdRef.current !== siteId) {
      hoveredSiteIdRef.current = siteId;

      if (hoverPopupRef.current) {
        hoverPopupRef.current.remove();
        hoverPopupRef.current = null;
      }

      hoverPopupRef.current = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 14,
      })
        .setLngLat(e.lngLat)
        .setHTML(buildPopupHtml(site, siteViolationCount, workersCount))
        .addTo(map);
    } else {
      if (hoverPopupRef.current) hoverPopupRef.current.setLngLat(e.lngLat);
    }
  };

  const onClick = async (e) => {
    const f = e?.features?.[0];
    if (!f) return;

    const site = getSiteFromFeature(f);
    if (!site) return;

    if (hoverPopupRef.current) {
      hoverPopupRef.current.remove();
      hoverPopupRef.current = null;
    }

    setSelectedSite(site);
    await fetchWorkersForSite(site);
  };

  // Attach only after map is loaded + layers exist
  const attach = () => {
    clickableLayers.forEach((layerId) => {
      // IMPORTANT: do NOT call map.getLayer here (it can crash during transitions).
      // Just attempt to register; if it fails, we skip.
      safeOn(layerId, "mouseenter", onMouseEnter);
      safeOn(layerId, "mouseleave", onMouseLeave);
      safeOn(layerId, "mousemove", onMouseMove);
      safeOn(layerId, "click", onClick);
    });
  };

  if (map.loaded()) {
    attach();
  } else {
    map.once("load", attach);
  }

  return () => {
    // Remove popup
    if (hoverPopupRef.current) {
      try {
        hoverPopupRef.current.remove();
      } catch (e) {}
      hoverPopupRef.current = null;
    }

    // Remove listeners safely (NO getLayer calls)
    clickableLayers.forEach((layerId) => {
      safeOff(layerId, "mouseenter", onMouseEnter);
      safeOff(layerId, "mouseleave", onMouseLeave);
      safeOff(layerId, "mousemove", onMouseMove);
      safeOff(layerId, "click", onClick);
    });
  };
}, []);


  // Count number of workers and compute counts per site
  useEffect(() => {
    const fetchWorkersCounts = async () => {
      try {
        // Only workers
        const q = query(collection(db, "users"), where("role", "==", "worker"));
        const snap = await getDocs(q);

        const counts = {};
        snap.docs.forEach((d) => {
          const u = d.data();
          const siteId = u?.assignedSiteId;
          if (!siteId) return;
          counts[siteId] = (counts[siteId] || 0) + 1;
        });

        setWorkersCountBySite(counts);
      } catch (err) {
        console.error("Failed to fetch workers counts:", err);
      }
    };

    fetchWorkersCounts();
  }, []);

  // Fetch the worker's violation
  const getWorkerViolationsForToday = ({ site, clockIn, clockOut }) => {
    const violations = [];
    const todayDay = new Date().getDay(); // 0=Sun ... 6=Sat
    const isWeekday = todayDay >= 1 && todayDay <= 5;

    const siteStart = site?.startTime || "08:00";
    const siteEnd = site?.endTime || "17:00";

    // Use your existing helper (supports {lat,lng} and GeoPoint)
    const coords = getSiteLatLng(site);
    const siteLat = coords?.lat;
    const siteLng = coords?.lng;

    // ABSENT (Mon-Fri only): no clocking at all during the shift
    if (isWeekday && !clockIn && !clockOut) {
      violations.push("Absent");
    }

    // 1) Clock-In outside 1km zone
    const distanceLimitKm = 1;
    if (
      clockIn &&
      Number.isFinite(siteLat) &&
      Number.isFinite(siteLng) &&
      clockIn.locationCoords?.lat != null &&
      clockIn.locationCoords?.lng != null
    ) {
      const dist = calculateDistanceKm(
        siteLat,
        siteLng,
        Number(clockIn.locationCoords.lat),
        Number(clockIn.locationCoords.lng)
      );
      if (dist > distanceLimitKm) violations.push("Clock-In outside zone");
    }

    // 2) Late clock-in (> 10 mins after siteStart)
    if (clockIn?.time) {
      const inHHMMSS = toTimeHHMMSS(clockIn.time);
      const startHHMMSS = toTimeHHMMSS(siteStart);
      if (inHHMMSS && startHHMMSS) {
        const diffMinutes = (hhmmssToDate(inHHMMSS) - hhmmssToDate(startHHMMSS)) / 60000;
        if (diffMinutes > 10) violations.push("Late Clock-In");
      }
    }

    // 3) Late clock-out / early clock-out
    if (clockOut?.time) {
      const outHHMMSS = toTimeHHMMSS(clockOut.time);
      const endHHMMSS = toTimeHHMMSS(siteEnd);
      if (outHHMMSS && endHHMMSS) {
        const diffMinutes = (hhmmssToDate(outHHMMSS) - hhmmssToDate(endHHMMSS)) / 60000;
        if (diffMinutes > 10) violations.push("Late Clock-Out");
        else if (diffMinutes < 0) violations.push("Early Clock-Out");
      }
    }

    return violations;
  };

  useEffect(() => {
    sitesRef.current = sites;
  }, [sites]);

  useEffect(() => {
    violationsRef.current = violationsBySite;
  }, [violationsBySite]);

  // Fetch Site worker informations (name, presence, absence ... etc)
  const fetchWorkersForSite = async (site) => {
    try {
      const todayDates = buildTodayDateConstraints();

      // 1) Fetch workers assigned to this site
      const workersQ = query(
        collection(db, "users"),
        where("role", "==", "worker"),
        where("assignedSiteId", "==", site.id)
      );

      const workersSnap = await getDocs(workersQ);

      // 2) For each worker, fetch today's clocking entries
      const workers = await Promise.all(
        workersSnap.docs.map(async (wDoc) => {
          const w = wDoc.data();
          const historyQ =
            todayDates.length === 1
              ? query(
                  collection(db, "users", wDoc.id, "clocking history"),
                  where("date", "==", todayDates[0])
                )
              : query(
                  collection(db, "users", wDoc.id, "clocking history"),
                  where("date", "in", todayDates)
                );
          const histSnap = await getDocs(historyQ);
          const entries = histSnap.docs.map((d) => d.data());

          const clockIn = pickClockEntry(entries, "Clock In");
          const clockOut = pickClockEntry(entries, "Clock Out");

          const presence = clockOut
            ? "OFF"
            : clockIn
            ? "Working"
            : "Absent";

          const clockInTime = clockIn?.time || "";
          const clockOutTime = clockOut?.time || "";

          const clockInTimeDisplay = clockInTime ? toDisplayHHMM(clockInTime) : "";
          const clockOutTimeDisplay = clockOutTime ? toDisplayHHMM(clockOutTime) : "";
          const hoursWorked = computeHoursWorked(clockInTime, clockOutTime);

          const violations = getWorkerViolationsForToday({
            site,
            clockIn,
            clockOut,
          });

          return {
            id: wDoc.id,
            fullName: w.fullName || "",
            email: w.email || "",
            phone: w.phone || "",
            presence,
            clockInTime: clockInTimeDisplay,
            clockOutTime: clockOutTimeDisplay,
            hoursWorked,
            violations,
          };
        })
      );

      // Optional: sort - working first
      workers.sort((a, b) => {
        const rank = (p) => (p === "Working" ? 0 : p === "OFF" ? 1 : 2);
        return rank(a.presence) - rank(b.presence);
      });

      setSiteWorkers(workers);
    } catch (err) {
      console.error("Failed to fetch site workers:", err);
      setSiteWorkers([]);
    }
  };

  // Calculate violations
  useEffect(() => {
    if (!sites || sites.length === 0) return;

    const fetchViolationsBySite = async () => {
      try {
        const todayDates = buildTodayDateConstraints();

        // Build a fast lookup for sites
        const sitesById = {};
        sites.forEach((s) => {
          sitesById[s.id] = s;
        });

        // Get all workers (only once)
        const workersQ = query(collection(db, "users"), where("role", "==", "worker"));
        const workersSnap = await getDocs(workersQ);

        const next = {}; // {siteId: {count}}

        // helper to increment
        const addSiteViolation = (siteId) => {
          if (!siteId) return;
          if (!next[siteId]) next[siteId] = { count: 0 };
          next[siteId].count += 1;
        };

        const todayDay = new Date().getDay(); // 0=Sun ... 6=Sat
        const isWeekday = todayDay >= 1 && todayDay <= 5;

        for (const wDoc of workersSnap.docs) {
          const w = wDoc.data();
          const siteId = w?.assignedSiteId;
          if (!siteId) continue;

          const site = sitesById[siteId];
          if (!site) continue;

          const siteStart = site?.startTime || "08:00";
          const siteEnd = site?.endTime || "17:00";
          const coords = getSiteLatLng(site);
          const siteLat = coords?.lat;
          const siteLng = coords?.lng;

          // Today's history for this worker
          const histQ =
            todayDates.length === 1
              ? query(
                  collection(db, "users", wDoc.id, "clocking history"),
                  where("date", "==", todayDates[0])
                )
              : query(
                  collection(db, "users", wDoc.id, "clocking history"),
                  where("date", "in", todayDates)
                );
          const histSnap = await getDocs(histQ);
          const entries = histSnap.docs.map((d) => d.data());

          const clockIn = pickClockEntry(entries, "Clock In");
          const clockOut = pickClockEntry(entries, "Clock Out");

          // ---- Violation checks (same logic as AdminAnalytics) ----
          let hasAnyViolation = false;

          // ABSENT (Mon-Fri only): no clocking at all during the shift
          if (isWeekday && !clockIn && !clockOut) hasAnyViolation = true;

          // 1) Clock-in outside 1km zone
          const distanceLimitKm = 1;
          if (
            clockIn &&
            Number.isFinite(siteLat) &&
            Number.isFinite(siteLng) &&
            clockIn.locationCoords?.lat != null &&
            clockIn.locationCoords?.lng != null
          ) {
            const dist = calculateDistanceKm(
              siteLat,
              siteLng,
              Number(clockIn.locationCoords.lat),
              Number(clockIn.locationCoords.lng)
            );
            if (dist > distanceLimitKm) hasAnyViolation = true;
          }

          // 2) Late clock-in (> 10 mins after siteStart)
          if (clockIn?.time) {
            const inHHMMSS = toTimeHHMMSS(clockIn.time);
            const startHHMMSS = toTimeHHMMSS(siteStart);
            if (inHHMMSS && startHHMMSS) {
              const diffMinutes = (hhmmssToDate(inHHMMSS) - hhmmssToDate(startHHMMSS)) / 60000;
              if (diffMinutes > 10) hasAnyViolation = true;
            }
          }

          // 3) Late/Early clock-out
          if (clockOut?.time) {
            const outHHMMSS = toTimeHHMMSS(clockOut.time);
            const endHHMMSS = toTimeHHMMSS(siteEnd);
            if (outHHMMSS && endHHMMSS) {
              const diffMinutes = (hhmmssToDate(outHHMMSS) - hhmmssToDate(endHHMMSS)) / 60000;
              if (diffMinutes > 10) hasAnyViolation = true;
              else if (diffMinutes < 0) hasAnyViolation = true;
            }
          }

          // If worker violated anything today -> count for that site (1 worker = +1)
          if (hasAnyViolation) addSiteViolation(siteId);
        }

        setViolationsBySite(next);
      } catch (err) {
        console.error("Failed to compute violationsBySite:", err);
        setViolationsBySite({});
      }
    };

    fetchViolationsBySite();
  }, [sites]);


  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100vh",
        overflow: "hidden",
        backgroundColor: "#f7f7f7",
        borderRadius: 5,
      }}
    >
      {/* LEFT: Tickets sidebar */}
      <SiteWorkersSidebar
        open={Boolean(selectedSite)}
        onClose={() => {
          setSelectedSite(null);
          setSiteWorkers([]);
        }}
        siteName={selectedSite?.name}
        siteAddress={selectedSite?.location?.address}
        workers={siteWorkers}
        onOpenHistory={handleOpenHistory}
      />

      {/* RIGHT: Map */}
      <Box
        ref={mapContainerRef}
        sx={{
          flex: 1,
          height: "100%",
          width: "100%",
        }}
      />

      <WorkerClockingHistoryModal
        open={openHistoryModal}
        onClose={() => setOpenHistoryModal(false)}
        userId={selectedHistoryUserId}
        sites={sites}                 
        workerProfileImg={workerProfile}
        emptyImg={emptyImage}
        t={t}
      />

      <Dialog
        open={openFilterModal}
        onClose={() => setOpenFilterModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          {t("Filter sites")}
        </DialogTitle>

        <DialogContent dividers>
        <Box sx={{ mb: 1.5 }}>
          <TextField
            value={siteFilterQuery}
            onChange={(e) => setSiteFilterQuery(e.target.value)}
            placeholder={t("Search site...")}
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "#fff",
              },
            }}
          />
        </Box>
          {sites?.length === 0 ? (
            <Typography sx={{ color: "#6b7280", fontSize: 14 }}>
              {t("No sites found")}
            </Typography>
          ) : (
            <List sx={{ py: 0 }}>
              {sites
                .filter((site) => {
                  const q = siteFilterQuery.trim().toLowerCase();
                  if (!q) return true;

                  const name = String(site?.name || "").toLowerCase();
                  const address = String(site?.location?.address || "").toLowerCase();

                  return name.includes(q) || address.includes(q);
                })
                .map((site) => {
                  const hidden = isSiteHidden(site.id);
                  return (
                    <ListItem
                      key={site.id}
                      disableGutters
                      secondaryAction={
                        <Switch
                          checked={!hidden}
                          onChange={() => toggleSiteHidden(site.id)}
                        />
                      }
                      sx={{
                        py: 1,
                        borderBottom: "1px solid #f3f4f6",
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#111827" }}>
                            {site?.name || t("Construction Site")}
                          </Typography>
                        }
                        secondary={
                          <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
                            {site?.location?.address || t("No address provided")}
                          </Typography>
                        }
                      />
                    </ListItem>
                  );
                })}
            </List>
          )}
        </DialogContent>

        <DialogActions>
          <Stack direction="row" spacing={1} sx={{ px: 1, pb: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setHiddenSiteIds([])}
              sx={{ borderRadius: 2 }}
            >
              {t("Show all")}
            </Button>
            <Button
              variant="contained"
              onClick={() => setOpenFilterModal(false)}
              sx={{ borderRadius: 2, bgcolor: "#c93d3d", "&:hover": { bgcolor: "#b53636" } }}
            >
              {t("Close")}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
